/* eslint-disable no-nested-ternary */
import {
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import DeclaracaoImportacaoContext from '../../../../../contexts/import/declaracao-importacao/DeclaracaoImportacaoContext';
import { selectDeclaracaoImportacao } from '../../../../../features/declaracao-importacao/declaracaoImportacaoSelectors';
import { fetchAllAsync as fetchAllMoedasAsync, selectMoedas } from '../../../../../features/moeda/moedaSlice';
import { fetchAdicoesByDiDuimpIdAsync } from '../../../../../features/declaracao-importacao/declaracaoImportacaoThunks';
import { declaracaoImportacaoAPI } from '../../../../../features/declaracao-importacao/declaracaoImportacaoAPI';

import QCXFormStepWizard from '../../../../../shared-components/form-step-wizard/QCXFormStepWizard';
import QCXFormTitle from '../../../../../components/form-title/QCXFormTitle';
import QCXSelectIncotermCondicaoVendaAutocomplete from '../../../../../shared-components/final-select-incoterm-condicao-venda-field/QCXSelectIncotermCondicaoVendaAutocomplete';
import QCXFinalTextField from '../../../../../shared-components/final-text-field/QCXFinalTextField';
import QCXFinalNumericDecimalField from '../../../../../shared-components/final-numeric-decimal-field/QCXFinalNumericDecimalField';
import QCXTextField from '../../../../../shared-components/text-field/QCXTextField';

import { normalizeNumeral, unnormalizeNumeral, formatDate } from '../../../../../utils/general/general-utils';
import { formatBrazilianNumericDecimal } from '../../../../../utils/hooks/form/field/formatters';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  tableHead: {
    '& th': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.common.white,
      fontWeight: 700,
      fontSize: 11,
      padding: '6px 8px',
    },
  },
  tableRowLabel: {
    '& td:first-child': {
      fontWeight: 700,
      fontSize: 12,
      backgroundColor: theme.palette.grey[100],
      padding: '4px 8px',
      minWidth: 130,
    },
  },
  tableCell: {
    padding: '4px 6px',
    '& .MuiInputBase-root': {
      fontSize: 12,
    },
  },
  groupBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
  },
  groupTitle: {
    fontWeight: 700,
    fontSize: 13,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(1),
    paddingBottom: 4,
    color: theme.palette.primary.main,
  },
}));

const fmtR = (val) => unnormalizeNumeral(val ?? 0, formatBrazilianNumericDecimal(2));
const fmtQ = (val) => unnormalizeNumeral(val ?? 0, formatBrazilianNumericDecimal(7));
const sumAdicoes = (adicoes, getter) =>
  (adicoes || []).reduce((acc, a) => acc + (normalizeNumeral(getter(a)) || 0), 0);

const VisaoGeralFormStepWizard = ({ ...restProps }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    idDiSelecionada,
    idFaturaSelecionada,
    fetchFaturaDiDuimpCapaByFaturaId: fetchById,
  } = useContext(DeclaracaoImportacaoContext);

  const di = useSelector(selectDeclaracaoImportacao);
  const moedas = useSelector(selectMoedas);
  const adicoes = useMemo(() => di?.atributosAdicionais?.adicoes || [], [di]);

  const [acrescimosDeducoesTotais, setAcrescimosDeducoesTotais] = useState(null);

  useEffect(() => {
    if (idFaturaSelecionada) {
      fetchById(idFaturaSelecionada);
    }
  }, [idFaturaSelecionada]);

  useEffect(() => {
    dispatch(fetchAllMoedasAsync());
  }, []);

  useEffect(() => {
    if (idDiSelecionada && !adicoes.length) {
      dispatch(fetchAdicoesByDiDuimpIdAsync(idDiSelecionada));
    }
  }, [idDiSelecionada]);

  useEffect(() => {
    const diDuimpId = di?.atributosAdicionais?.id;

    if (!diDuimpId) return;

    declaracaoImportacaoAPI.fetchTotaisAcrescimosDeducoes(diDuimpId)
      .then((resp) => {
        const {
          totalAcrescimoDolar,
          totalAcrescimoReal,
          totalDeducaoDolar,
          totalDeducaoReal,
          taxaMoedaAcrescimo,
          moedaAcrescimoId,
          totalAcrescimoMoeda,
          taxaMoedaDeducao,
          moedaDeducaoId,
          totalDeducaoMoeda,
        } = resp.data;

        setAcrescimosDeducoesTotais({
          acrescimo: {
            valorReal: fmtR(totalAcrescimoReal),
            valorDolar: fmtR(totalAcrescimoDolar),
            taxaMoeda: taxaMoedaAcrescimo,
            moeda: moedaAcrescimoId,
            valorMoeda: totalAcrescimoMoeda,
          },
          deducao: {
            valorReal: fmtR(totalDeducaoReal),
            valorDolar: fmtR(totalDeducaoDolar),
            taxaMoeda: taxaMoedaDeducao,
            moeda: moedaDeducaoId,
            valorMoeda: totalDeducaoMoeda,
          },
        });
      })
      .catch(() => { });
  }, [di?.atributosAdicionais?.id]);

  const getMoedaLabel = (moedaOrId) => {
    const id = moedaOrId && typeof moedaOrId === 'object' ? moedaOrId?.id : moedaOrId;
    if (!id) return '-';
    const m = moedas.find((x) => x.id === id || x.id === Number(id));
    return m ? `${String(m.code).padStart(3, '0')} - ${m.description}` : String(id);
  };

  const tributos = useMemo(() => {
    const ii = sumAdicoes(adicoes, (a) => a.ii?.valorRecolherReal);
    const ipi = sumAdicoes(adicoes, (a) => a.ipi?.valorRecolherReal);
    const pis = sumAdicoes(adicoes, (a) => a.pisCofins?.valorRecolherPisPasepReal);
    const cofins = sumAdicoes(adicoes, (a) => a.pisCofins?.valorRecolherCofinsReal);
    const taxaSiscomex = sumAdicoes(adicoes, (a) => a.taxaSiscomex?.valorReal ?? a.taxaSiscomex?.valorRecolherReal);
    const antidumping = sumAdicoes(adicoes, (a) => a.antidumping?.valorReal ?? a.antidumping?.valorRecolherReal);
    const cide = sumAdicoes(adicoes, (a) => a.cide?.valorRecolherReal ?? a.cide?.valorReal);
    const ibs = sumAdicoes(adicoes, (a) => a.ibs?.valorRecolherReal);
    const cbs = sumAdicoes(adicoes, (a) => a.cbs?.valorRecolherReal);
    const total = ii + ipi + pis + cofins + taxaSiscomex + antidumping + cide + ibs + cbs;
    return {
      ii: fmtR(ii), ipi: fmtR(ipi), pis: fmtR(pis), cofins: fmtR(cofins),
      taxaSiscomex: fmtR(taxaSiscomex), antidumping: fmtR(antidumping),
      cide: fmtR(cide), ibs: fmtR(ibs), cbs: fmtR(cbs), total: fmtR(total),
    };
  }, [adicoes]);

  const icms = useMemo(() => {
    const firstAdicao = adicoes[0];
    const baseCalcIntegral = sumAdicoes(adicoes, (a) => a.icms?.baseCalculoReal);
    const valorDevido = sumAdicoes(adicoes, (a) => a.icms?.valorDevidoReal);
    const valorRecolher = sumAdicoes(adicoes, (a) => a.icms?.valorRecolherReal);
    const percReducao = firstAdicao?.icms?.percentualReducao ?? 0;
    const baseCalcReduzida = normalizeNumeral(firstAdicao?.icms?.baseCalculoReal ?? 0) * (1 - normalizeNumeral(percReducao) / 100);
    const fecpVal = sumAdicoes(adicoes, (a) => {
      const alqFecp = normalizeNumeral(a.icms?.aliquotaFecp ?? 0);
      const bc = normalizeNumeral(a.icms?.baseCalculoReal ?? 0);
      return (alqFecp / 100) * bc;
    });
    const subTributaria = sumAdicoes(adicoes, (a) => a.icms?.recolherPermanencia);
    const valorBeneficio = valorDevido - valorRecolher;

    return {
      aliquota: fmtQ(firstAdicao?.icms?.aliquota),
      percentualReducao: fmtQ(percReducao),
      baseCalculoIntegral: fmtR(baseCalcIntegral),
      tipoReducao: firstAdicao?.icms?.tipoReducao || '-',
      valorDevido: fmtR(valorDevido),
      baseCalculoReduzida: fmtR(baseCalcReduzida),
      valorBeneficio: fmtR(valorBeneficio),
      fecp: fmtR(fecpVal),
      valorRecolher: fmtR(valorRecolher),
      subTributaria: fmtR(subTributaria),
    };
  }, [adicoes]);

  const normalizedDi = useMemo(() => ({
    atributosAdicionais: {
      processo: di?.atributosAdicionais?.processo || '',
      adicoesCount: String((di?.atributosAdicionais?.adicoes || []).length),
      dataCalculo: di?.atributosAdicionais?.dataCalculo ? formatDate(di.atributosAdicionais.dataCalculo) : (di?.data ? formatDate(di.data) : '-'),
    },
    incotermCondicaoVenda: di?.incotermCondicaoVenda,
    pesoLiquido: fmtQ(di?.pesoLiquido),
    pesoBruto: fmtQ(di?.pesoBruto),
    // dataTaxa: di?.data ? formatDate(di.data) : (di?.atributosAdicionais?.dataRegistroSiscomex ? formatDate(di.atributosAdicionais.dataRegistroSiscomex) : '-'),
    afrmm: {
      valorReal: fmtR(di?.totaisAfrmm?.valorReal),
    },
    multaLi: fmtR(sumAdicoes(di?.atributosAdicionais?.adicoes, (a) => a.multaLi?.valorDevidoReal ?? a.multa?.valorDevidoReal)),
    despesasAduaneiras: fmtR(di?.despesasAcessorias?.valorReal),
  }), [di]);

  const valoresTable = useMemo(() => [
    {
      label: 'Valor MLE',
      moeda: getMoedaLabel(di?.mle?.moeda),
      taxa: fmtQ(di?.mle?.taxaMoeda),
      valorMoeda: fmtR(di?.mle?.valorMoeda),
      valorReal: fmtR(di?.mle?.valorReal),
      valorDolar: fmtR(di?.mle?.valorDolar),
    },
    {
      label: 'Frete Internacional',
      moeda: getMoedaLabel(di?.frete?.moeda),
      taxa: fmtQ(di?.frete?.taxaMoeda),
      valorMoeda: fmtR((normalizeNumeral(di?.frete?.valorMoedaPrepaid) || 0) + (normalizeNumeral(di?.frete?.valorMoedaCollect) || 0)),
      valorReal: fmtR((normalizeNumeral(di?.frete?.valorRealPrepaid) || 0) + (normalizeNumeral(di?.frete?.valorRealCollect) || 0)),
      valorDolar: fmtR((normalizeNumeral(di?.frete?.valorDolarPrepaid) || 0) + (normalizeNumeral(di?.frete?.valorDolarCollect) || 0)),
    },
    {
      label: 'Frete Nacional',
      moeda: getMoedaLabel(di?.frete?.moeda),
      taxa: fmtQ(di?.frete?.taxaMoeda),
      valorMoeda: fmtR(di?.frete?.valorFreteNacionalMoeda),
      valorReal: fmtR(di?.frete?.valorFreteNacionalReal),
      valorDolar: fmtR(di?.frete?.valorFreteNacionalDolar),
    },
    {
      label: 'Seguro',
      moeda: getMoedaLabel(di?.seguro?.moeda),
      taxa: fmtQ(di?.seguro?.taxaMoeda),
      valorMoeda: fmtR(di?.seguro?.valorMoeda),
      valorReal: fmtR(di?.seguro?.valorReal),
      valorDolar: fmtR(di?.seguro?.valorDolar),
    },
  ], [di, moedas]);

  const despesasTable = useMemo(() => {
    const valorCIF = sumAdicoes(adicoes, (a) => a.ii?.baseCalculoReal);
    const valorCIFDolar = sumAdicoes(adicoes, (a) => a.ii?.baseCalculoDolar);
    const cifMoedaSum = sumAdicoes(adicoes, (a) => a.ii?.baseCalculoMoeda);
    const taxaMle = normalizeNumeral(di?.mle?.taxaMoeda) || 1;
    const valorCIFMoeda = cifMoedaSum > 0 ? cifMoedaSum : (taxaMle > 0 ? valorCIF / taxaMle : 0);

    const acrescimoValorMoeda = normalizeNumeral(acrescimosDeducoesTotais?.acrescimo?.valorMoeda) || 0;
    const deducaoValorMoeda = normalizeNumeral(acrescimosDeducoesTotais?.deducao?.valorMoeda) || 0;
    const acrescimoTaxa = acrescimosDeducoesTotais?.acrescimo?.taxaMoeda ? fmtQ(acrescimosDeducoesTotais.acrescimo.taxaMoeda) : '-';
    const deducaoTaxa = acrescimosDeducoesTotais?.deducao?.taxaMoeda ? fmtQ(acrescimosDeducoesTotais.deducao.taxaMoeda) : '-';
    const acrescimoMoeda = acrescimosDeducoesTotais?.acrescimo?.moeda ? getMoedaLabel(acrescimosDeducoesTotais.acrescimo.moeda) : '-';
    const deducaoMoeda = acrescimosDeducoesTotais?.deducao?.moeda ? getMoedaLabel(acrescimosDeducoesTotais.deducao.moeda) : '-';
    return [
      {
        label: 'Despesa',
        moeda: getMoedaLabel(di?.despesa?.moeda),
        taxa: fmtQ(di?.despesa?.taxaMoeda),
        valorMoeda: fmtR(di?.despesa?.valorMoeda),
        valorReal: fmtR(di?.despesa?.valorReal),
        valorDolar: fmtR(di?.despesa?.valorDolar),
      },
      {
        label: 'Desconto',
        moeda: getMoedaLabel(di?.desconto?.moeda),
        taxa: fmtQ(di?.desconto?.taxaMoeda),
        valorMoeda: fmtR(di?.desconto?.valorMoeda),
        valorReal: fmtR(di?.desconto?.valorReal),
        valorDolar: fmtR(di?.desconto?.valorDolar),
      },
      {
        label: 'Acréscimo',
        moeda: acrescimoMoeda,
        taxa: acrescimoTaxa,
        valorMoeda: acrescimoValorMoeda,
        valorReal: acrescimosDeducoesTotais?.acrescimo?.valorReal ?? '-',
        valorDolar: acrescimosDeducoesTotais?.acrescimo?.valorDolar ?? '-',
      },
      {
        label: 'Dedução',
        moeda: deducaoMoeda,
        taxa: deducaoTaxa,
        valorMoeda: deducaoValorMoeda,
        valorReal: acrescimosDeducoesTotais?.deducao?.valorReal ?? '-',
        valorDolar: acrescimosDeducoesTotais?.deducao?.valorDolar ?? '-',
      },
      {
        label: 'Valor CIF',
        moeda: getMoedaLabel(di?.mle?.moeda),
        taxa: fmtQ(di?.mle?.taxaMoeda),
        valorMoeda: fmtR(valorCIFMoeda),
        valorReal: fmtR(valorCIF),
        valorDolar: fmtR(valorCIFDolar),
      },
    ];
  }, [di, moedas, acrescimosDeducoesTotais]);

  const tableHeaders = ['', 'Moeda', 'Taxa', 'Valor na Moeda', 'Valor em Real', 'Valor em Dólar'];

  const renderTable = (rows) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {tableHeaders.map((h) => (
              <TableCell key={h} align={h === '' ? 'left' : 'center'}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label} className={classes.tableRowLabel}>
              <TableCell>{row.label}</TableCell>
              <TableCell className={classes.tableCell} align="right">
                <QCXTextField value={row.moeda} disabled size="small" />
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                <QCXTextField value={row.taxa} disabled size="small" />
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                <QCXTextField value={row.valorMoeda} disabled size="small" />
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                <QCXTextField value={row.valorReal} disabled size="small" />
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                <QCXTextField value={row.valorDolar} disabled size="small" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <QCXFormStepWizard initialValues={normalizedDi} disableSubmitOnNext disablePristine onSubmit={() => null}>
      {({ values }) => (
        <Grid container spacing={2}>

          {/* BLOCO 1 — CABEÇALHO */}
          <Grid item xs={12}>
            <QCXFormTitle title="Informações Gerais" boxProps={{ pt: 0 }} />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* COLUNA ESQUERDA */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <QCXFinalTextField
                      id="field-numero-processo"
                      name="atributosAdicionais.processo"
                      label="Número do Processo"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <QCXFinalTextField
                      id="field-data-taxa"
                      name="atributosAdicionais.dataCalculo"
                      label="Data Taxa"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* COLUNA DIREITA */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <QCXFinalNumericDecimalField
                      id="field-peso-liquido"
                      name="pesoLiquido"
                      label={t('com.muralis.qcx.pesoLiquido')}
                      scale={5}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <QCXFinalNumericDecimalField
                      id="field-peso-bruto"
                      name="pesoBruto"
                      label={t('com.muralis.qcx.pesoBruto')}
                      scale={5}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <QCXFinalTextField
                      id="field-adicoes-count"
                      name="atributosAdicionais.adicoesCount"
                      label="Adições"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* <Grid item xs={12} sm={6} md={3}>
            <QCXSelectIncotermCondicaoVendaAutocomplete
              id="field-incoterm"
              key="field-incoterm"
              name="incotermCondicaoVenda.id"
              label={t('com.muralis.qcx.incoterm')}
              initialValues={values}
              disabled
            />
          </Grid> */}


          {/* BLOCO 2 — TABELAS */}
          <Grid item xs={12}>
            <QCXFormTitle title="Valores" boxProps={{ pt: 1 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700, textTransform: 'uppercase' }}>
              Valores
            </Typography>
            {renderTable(valoresTable)}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700, textTransform: 'uppercase' }}>
              Despesas
            </Typography>
            {renderTable(despesasTable)}
          </Grid>

          {/* BLOCO 3 — CAMPOS ISOLADOS */}
          <Grid item xs={12}>
            <QCXFormTitle title="Taxas e Despesas" boxProps={{ pt: 1 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <QCXFinalTextField
              id="field-afrmm"
              key="field-afrmm"
              name="afrmm.valorReal"
              label="AFRMM"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <QCXFinalTextField
              id="field-multa-li"
              key="field-multa-li"
              name="multaLi"
              label="Multa LI"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <QCXFinalTextField
              id="field-despesas-aduaneiras"
              key="field-despesas-aduaneiras"
              name="despesasAduaneiras"
              label="Despesas Aduaneiras"
              disabled
            />
          </Grid>

          {/* BLOCO 4 — TRIBUTOS + ICMS */}
          <Grid item xs={12}>
            <QCXFormTitle title="Tributos e ICMS" boxProps={{ pt: 1 }} />
          </Grid>

          {/* SUB-BLOCO 4.1 — TRIBUTOS */}
          <Grid item xs={12} md={6}>
            <div className={classes.groupBox}>
              <Typography className={classes.groupTitle}>Tributos</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <QCXTextField id="f-ii" label="Valor II" value={tributos.ii} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-antidumping" label="Antidumping" value={tributos.antidumping} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-ipi" label="Valor IPI" value={tributos.ipi} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-ibs" label="Valor IBS" value={tributos.ibs} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-pis" label="Valor PIS" value={tributos.pis} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-cbs" label="Valor CBS" value={tributos.cbs} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-cofins" label="Valor COFINS" value={tributos.cofins} disabled />
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={6}>
                  <QCXTextField id="f-taxa-siscomex" label="Taxa SISCOMEX" value={tributos.taxaSiscomex} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-total" label="Valor Total" value={tributos.total} disabled />
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* SUB-BLOCO 4.2 — ICMS */}
          <Grid item xs={12} md={6}>
            <div className={classes.groupBox}>
              <Typography className={classes.groupTitle}>ICMS</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <QCXTextField id="f-aliq-icms" label="Alíquota ICMS" value={icms.aliquota} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-perc-reducao" label="% Redução" value={icms.percentualReducao} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-bc-integral" label="Base Cálculo Integral" value={icms.baseCalculoIntegral} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-tipo-reducao" label="Tipo Redução" value={icms.tipoReducao} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-valor-devido" label="Valor Devido" value={icms.valorDevido} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-bc-reduzida" label="Base Cálculo Reduzida" value={icms.baseCalculoReduzida} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-valor-beneficio" label="Valor Benefício" value={icms.valorBeneficio} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-fecp" label="Valor FECP" value={icms.fecp} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-valor-recolher" label="Valor a Recolher" value={icms.valorRecolher} disabled />
                </Grid>
                <Grid item xs={6}>
                  <QCXTextField id="f-sub-tributaria" label="Valor Sub. Tributária" value={icms.subTributaria} disabled />
                </Grid>
              </Grid>
            </div>
          </Grid>

        </Grid>
      )}
    </QCXFormStepWizard>
  );
};

export default VisaoGeralFormStepWizard;
